import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { useEffect, useState } from "react";
import {
  SquaresFour, Robot, Scroll, Package, AddressBook, Funnel, PaperPlaneTilt, Tray, WarningDiamond,
  CalendarCheck, Receipt, GearSix, Power, Phone, BookOpen, PaintBrushBroad, RocketLaunch, Lightning, CurrencyCircleDollar,
} from "@phosphor-icons/react";
import { api, act } from "@/lib/api";
import { Badge } from "@/components/common";
import { MasaBrand } from "@/components/MasaBrand";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const NAV = [
  ["Command", [["/one", "MaSa One", Lightning], ["/launchpad", "Launchpad", RocketLaunch], ["/guide", "How to use", BookOpen], ["/", "Outcomes", SquaresFour], ["/manager", "AI Manager", Robot], ["/exceptions", "Exceptions", WarningDiamond]]],
  ["Sales engine", [["/contacts", "Contacts", AddressBook], ["/segments", "Segments", Funnel], ["/campaigns", "Campaigns", PaperPlaneTilt], ["/inbox", "Shared inbox", Tray], ["/calls", "AI calls", Phone], ["/meetings", "Meetings & pipeline", CalendarCheck]]],
  ["Marketing engine", [["/studio", "Content studio", PaintBrushBroad]]],
  ["Boundaries", [["/charter", "Operating charter", Scroll], ["/products", "Products & knowledge", Package]]],
  ["Governance", [["/costs", "Monthly costs", CurrencyCircleDollar], ["/audit", "Audit & spend", Receipt], ["/settings", "Settings", GearSix]]],
];

const KillSwitch = ({ paused, onChange }) => {
  const [open, setOpen] = useState(false);
  const go = async () => {
    const r = await act(() => api.post("/settings/pause", { paused: !paused }), paused ? "Operations resumed" : "All agent work paused");
    if (r) onChange(r.paused);
  };
  return (
    <>
      <button data-testid="kill-switch-btn" onClick={() => setOpen(true)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${paused ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"}`}>
        <Power weight="bold" size={14} /> {paused ? "PAUSED — resume" : "Kill switch"}
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#121212] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{paused ? "Resume all agent operations?" : "Pause the whole tenant?"}</AlertDialogTitle>
            <AlertDialogDescription>{paused ? "Paused sequences resume; agents can act within the charter again." : "Every active sequence pauses and no agent can send, launch or spend until resumed."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kill-switch-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="kill-switch-confirm" onClick={go} className={paused ? "" : "bg-[#FF3B30] hover:bg-[#FF6961]"}>{paused ? "Resume" : "Pause everything"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default function Layout({ children }) {
  const [s, setS] = useState({});
  const [charter, setCharter] = useState(null);
  const [exc, setExc] = useState(0);
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/me").then((r) => setMe(r.data)).catch(() => setMe({})); }, []);
  const pending = me?.role === "pending";
  useEffect(() => {
    if (!me || pending) return undefined;
    const load = () => {
      api.get("/settings").then((r) => setS(r.data)).catch(() => {});
      api.get("/charter").then((r) => setCharter(r.data.active)).catch(() => {});
      api.get("/exceptions").then((r) => setExc(r.data.length)).catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("exceptions-changed", load);
    return () => { clearInterval(t); window.removeEventListener("exceptions-changed", load); };
  }, [me, pending]);
  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      <aside className="w-60 shrink-0 border-r border-border hidden md:flex flex-col sticky top-0 h-screen">
        <div className="px-5 h-14 flex items-center border-b border-border"><MasaBrand /></div>
        <nav className={`flex-1 overflow-y-auto py-4 space-y-5 ${pending ? "hidden" : ""}`}>
          {NAV.map(([group, items]) => (
            <div key={group}>
              <div className="px-5 mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{group}</div>
              {items.map(([to, label, Icon]) => (
                <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-sm border-l-2 transition-colors ${isActive ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-[#121212]"}`}>
                  <Icon size={17} />{label}
                  {label === "Exceptions" && exc > 0 && <span className="ml-auto text-[10px] font-mono bg-[#FFB020] text-black rounded-sm px-1.5" data-testid="nav-exception-count">{exc}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-border text-[11px] text-zinc-500 font-mono">MaSa · by Specia AI</div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="h-14 border-b border-border flex items-center justify-between px-6 sticky top-0 bg-[#0A0A0A] z-30">
          <div className={`flex items-center gap-3 text-xs text-zinc-400 ${pending ? "invisible" : ""}`} data-testid="topbar-charter-status">
            <span className="font-mono uppercase tracking-wider">Charter</span>
            {charter ? <><Badge v="approved" /> <span className="font-mono">v{charter.version}</span> <Badge v={charter.autonomy_mode} /></> : <Badge v="not approved" tone="red" />}
          </div>
          <div className="flex items-center gap-4">
            {!pending && me && <KillSwitch paused={!!s.paused} onChange={(p) => setS({ ...s, paused: p })} />}
            <UserButton />
          </div>
        </header>
        {s.paused && <div className="bg-[#FF3B30] text-white text-xs font-semibold px-6 py-2" data-testid="paused-banner">Tenant paused by {s.paused_by}. No agent sends, launches or spend until resumed.</div>}
        <main className="p-6 lg:p-8 max-w-[1600px]">
          {me?.blocked ? (
            <div className="max-w-lg mt-16 border border-border bg-[#121212] rounded-md p-8" data-testid="access-blocked">
              <h1 className="text-2xl font-extrabold">This account doesn't have access</h1>
              <p className="text-sm text-zinc-400 mt-3">You're signed in as {me.email}. This workspace is private. Ask the owner to add your email to the allowed list.</p>
            </div>
          ) : me?.role === "pending" ? (
            <div className="max-w-lg mt-16 border border-border bg-[#121212] rounded-md p-8" data-testid="pending-approval">
              <h1 className="text-2xl font-extrabold">Access awaiting approval</h1>
              <p className="text-sm text-zinc-400 mt-3">You're signed in as {me.email}. An admin must approve your account in Settings → Users & roles before you can see contacts or act on the platform.</p>
            </div>
          ) : me ? children : null}
        </main>
      </div>
    </div>
  );
}
